export class TimeoutError extends Error {
    constructor(message: string) {
        super(message);
        this.name = "TimeoutError";
    }
}

/**
 * Promisified sleep that you can `await`.
 *
 * @param millis Time to sleep in milliseconds
 * @returns a Promise that resolves when sleeping is done
 */
export function sleep(millis: number): Promise<void> {
    return new Promise((resolve) => {
        setTimeout(resolve, millis);
    });
}

/**
 * Wraps a promise with a timeout. Rejects with a {@link TimeoutError} if the promise
 * does not settle within `timeoutMs` milliseconds.
 */
export function withTimeout<T>(promise: Promise<T>, timeoutMs: number, label: string): Promise<T> {
    return new Promise((resolve, reject) => {
        const timer = setTimeout(() => {
            reject(new TimeoutError(`${label} timed out after ${timeoutMs / 1000}s`));
        }, timeoutMs);

        promise.then(
            (value) => {
                clearTimeout(timer);
                resolve(value);
            },
            (err: unknown) => {
                clearTimeout(timer);
                reject(err);
            },
        );
    });
}

/**
 * Polls `predicate` until it returns true or `timeoutMs` elapses. Checks immediately, then after each
 * `intervalMs` wait. Throws an Error that includes `label` when the deadline is reached.
 */
export async function pollUntil(
    predicate: () => Promise<boolean> | boolean,
    label: string,
    timeoutMs: number,
    intervalMs = 100,
): Promise<void> {
    const deadline = Date.now() + timeoutMs;
    while (!(await predicate())) {
        if (Date.now() > deadline) {
            throw new Error(`timed out waiting for: ${label}`);
        }
        await sleep(intervalMs);
    }
}

/**
 * Repeatedly calls `fn` after each delay until it returns a truthy value or the attempt limit is reached.
 * Async callbacks are awaited; retries use the resolved value's truthiness.
 * Returns the last result from `fn`.
 */
export async function attemptOnDelay<T>(
    delayMs: number,
    attemptLimit: number,
    fn: (...args: unknown[]) => T,
    ...args: unknown[]
): Promise<Awaited<T> | false> {
    let attempts = 0;
    let result: Awaited<T> | false = false;
    while (attempts < attemptLimit && !result) {
        attempts++;
        await sleep(delayMs);
        try {
            result = await Promise.resolve(fn(...args));
        } catch {
            // pass
        }
    }
    return result;
}
